import { createEffect, untrack } from "solid-js"
import { Group, Mesh, type Object3D } from "three"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import applyEntityWalkPathToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityWalkPathToNextObject3DFrame"
import type { LightTankEntity } from "../../core/entity/EntityType/LightTank"
import shouldEntityShowFadedFrames from "../../core/entity/shouldEntityShowFadedFrames"
import tmpRng from "../../core/rng/tmpRng"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import assertClassInDev from "../../ldsh/assertClassInDev"
import { M10Booker1_M10Booker1_GLTF_Model } from "../gltf_models.generated"
import { appGltfLoader } from "./appGltfLoader"
import applyPlayerColors from "./applyPlayerColors"
import creaseMeshGeometryNormals from "./creaseMeshGeometryNormals"
import cubicEaseInOutBounce from "./cubicEaseInOutBounce"
import onGltfLoaderError from "./onGltfLoaderError"
import runWithObject3DOwner from "./runWithObject3DOwner"

const totalLoopMs = 1000

// const heights = [1, 2, 2, 3, 3, 4]

export default function makeLightTankObject3D (
  engine: Engine,
  ent: EntityGraphNode<LightTankEntity>,
  callback: (newGroup: Object3D) => void
): void {
  return appGltfLoader.load(M10Booker1_M10Booker1_GLTF_Model, function (ModelObj) {
    tmpRng.seed = ent.id
    const { scene } = ModelObj
    // printObjectHierarchy(scene)

    const bodyGroup = scene.getObjectByName('Body') as Group
    assertClassInDev(bodyGroup, Group)

    const turretGroup = scene.getObjectByName('Turret') as Group
    assertClassInDev(turretGroup, Group)

    const barrelMesh = scene.getObjectByName('Barrel') as Mesh
    creaseMeshGeometryNormals(barrelMesh)
    assertClassInDev(barrelMesh, Mesh)

    // turretGroup.position.y -= 2
    // turretGroup.position.y = 4
    // barrelMesh.position.y = -0.5

    const leftTrackMesh = scene.getObjectByName('LeftTrack') as Mesh
    creaseMeshGeometryNormals(leftTrackMesh)
    assertClassInDev(leftTrackMesh, Mesh)

    const rightTrackMesh = leftTrackMesh.clone()
    rightTrackMesh.name = 'RightTrack'

    // scene.add(leftTrackMesh)
    scene.add(rightTrackMesh)

    rightTrackMesh.position.x = 9

    applyPlayerColors(engine, scene, ent)

    const defaultHullY = bodyGroup.position.y

    let entValue = untrack(ent.raw)
    runWithObject3DOwner(scene, () => {
      createEffect(() => {
        entValue = ent.raw()
      })
    })


    mountOnFrameUpdateToObject3D(engine, bodyGroup, (elapsedMs: number, frameAt: number): void => {
      const mod = frameAt % totalLoopMs
      if (shouldEntityShowFadedFrames(entValue)) {
        bodyGroup.position.y = defaultHullY
      } else if (entValue.player_id) {
        bodyGroup.position.y = defaultHullY + (cubicEaseInOutBounce(mod / 500) / 7)
      }
      applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
      applyEntityWalkPathToNextObject3DFrame(scene, elapsedMs)
    })

    callback(scene)
  }, undefined, onGltfLoaderError)
}
